// 音频管理器 - 放牛山2.0音效系统
// 使用Web Audio API生成简单音效，无需外部资源

export class AudioManager {
  private ctx: AudioContext | null = null;
  private enabled: boolean = true;
  private volume: number = 0.5;
  
  // 音效类型
  private soundEffects: Record<string, () => void> = {};
  
  constructor() {
    // 延迟初始化AudioContext（需要用户交互后才能播放）
    this.initOnFirstInteraction();
    this.initSounds();
  }
  
  private initOnFirstInteraction(): void {
    const initAudio = () => {
      if (!this.ctx) {
        this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.ctx?.state === 'suspended') {
        this.ctx.resume();
      }
    };
    
    // 监听首次交互
    ['click', 'touchstart', 'keydown'].forEach(event => {
      document.addEventListener(event, initAudio, { once: true });
    });
  }
  
  private initSounds(): void {
    // 硬币音效 - 清脆的叮声
    this.soundEffects.coin = () => {
      this.playTone(1200, 0.1, 'sine', 0.3);
      setTimeout(() => this.playTone(1600, 0.15, 'sine', 0.2), 50);
    };
    
    // 点击房子 - 木门声
    this.soundEffects.house = () => {
      this.playNoise(0.15, 0.2, 'lowpass', 400);
    };
    
    // 点击奶奶 - 温暖笑声
    this.soundEffects.grandma = () => {
      this.playTone(400, 0.1, 'sine', 0.2);
      setTimeout(() => this.playTone(500, 0.15, 'sine', 0.15), 100);
      setTimeout(() => this.playTone(450, 0.2, 'sine', 0.1), 200);
    };
    
    // 点击树 - 沙沙声
    this.soundEffects.tree = () => {
      this.playNoise(0.2, 0.15, 'bandpass', 800);
    };
    
    // 点击鸡 - 咯咯叫
    this.soundEffects.chicken = () => {
      this.playTone(800, 0.05, 'sawtooth', 0.15);
      setTimeout(() => this.playTone(600, 0.08, 'sawtooth', 0.1), 80);
    };
    
    // 点击狗 - 汪汪
    this.soundEffects.dog = () => {
      this.playTone(250, 0.15, 'sawtooth', 0.2);
      setTimeout(() => this.playTone(200, 0.2, 'sawtooth', 0.15), 150);
    };
    
    // 点击菜地 - 拔菜声
    this.soundEffects.garden = () => {
      this.playNoise(0.1, 0.1, 'highpass', 1000);
      setTimeout(() => this.playTone(600, 0.08, 'sine', 0.1), 50);
    };
    
    // 点击水井 - 水声
    this.soundEffects.well = () => {
      this.playNoise(0.3, 0.2, 'lowpass', 600);
      setTimeout(() => this.playTone(400, 0.15, 'sine', 0.15), 100);
    };
    
    // 点击石磨 - 摩擦声
    this.soundEffects.mill = () => {
      this.playNoise(0.2, 0.3, 'lowpass', 200);
    };
    
    // 冷却提示 - 短促低音
    this.soundEffects.cooldown = () => {
      this.playTone(150, 0.08, 'triangle', 0.15);
    };
    
    // 成就解锁 - 欢快音效
    this.soundEffects.achievement = () => {
      const notes = [523, 659, 784, 1047]; // C E G C
      notes.forEach((freq, i) => {
        setTimeout(() => this.playTone(freq, 0.15, 'sine', 0.2), i * 100);
      });
    };
  }
  
  // 播放基础音调
  private playTone(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    gain: number = 0.3
  ): void {
    if (!this.enabled || !this.ctx) return;
    
    const osc = this.ctx.createOscillator();
    const gainNode = this.ctx.createGain();
    
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, this.ctx.currentTime);
    
    gainNode.gain.setValueAtTime(gain * this.volume, this.ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + duration);
    
    osc.connect(gainNode);
    gainNode.connect(this.ctx.destination);
    
    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + duration);
  }
  
  // 播放噪音
  private playNoise(
    duration: number,
    gain: number = 0.2,
    filterType: BiquadFilterType = 'lowpass',
    filterFreq: number = 500
  ): void {
    if (!this.enabled || !this.ctx) return;
    
    const bufferSize = this.ctx.sampleRate * duration;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    
    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;
    
    const filter = this.ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = filterFreq;
    
    const gainNode = this.ctx.createGain();
    gainNode.gain.setValueAtTime(gain * this.volume, this.ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + duration);
    
    noise.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.ctx.destination);
    
    noise.start(this.ctx.currentTime);
  }
  
  // 播放音效
  play(soundName: string): void {
    const sound = this.soundEffects[soundName];
    if (sound) {
      try {
        sound();
      } catch (e) {
        console.warn('Audio play failed:', e);
      }
    }
  }
  
  // 设置启用状态
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
  
  // 设置音量
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }
  
  // 获取音量
  getVolume(): number {
    return this.volume;
  }
  
  // 是否启用
  isEnabled(): boolean {
    return this.enabled;
  }
}

// 导出单例
export const audioManager = new AudioManager();

// 播放特定物体音效
export function playObjectSound(objectType: string): void {
  const soundMap: Record<string, string> = {
    house: 'house',
    grandma: 'grandma',
    tree: 'tree',
    chicken: 'chicken',
    dog: 'dog',
    garden: 'garden',
    well: 'well',
    mill: 'mill',
    coop: 'coin',
    // 新增物体音效映射
    vegetable_patch: 'garden',
    water_well: 'well',
    stone_mill: 'mill',
    chicken_coop: 'chicken',
    farm_plot: 'garden',
    flower_bed: 'tree',
    haystack: 'coin',
    fence: 'house',
    basket: 'coin',
    stump: 'tree',
  };
  
  const soundName = soundMap[objectType] || 'coin';
  audioManager.play(soundName);
}

// 播放金币音效
export function playCoinSound(): void {
  audioManager.play('coin');
}

// 播放冷却提示
export function playCooldownSound(): void {
  audioManager.play('cooldown');
}

// 播放成就音效
export function playAchievementSound(): void {
  audioManager.play('achievement');
}
